import { Divider, LoadingDots } from '@advanza/ui'
import Card from 'components/Card'
import LogsList from 'components/logs/LogsList'
import MatchFilter from 'components/matches/MatchFilter'
import MatchList from 'components/matches/MatchList'
import NotesFilter from 'components/notes/NotesFilter'
import NotesList from 'components/notes/NotesList'
import ReclaimDetails from 'components/reclaims/ReclaimDetails'
import ReclaimsList from 'components/reclaims/ReclaimsList'
import ReclaimTaskDetails from 'components/reclaims/ReclaimTaskDetails'
import AddMatchManuallyButton from 'components/requests/AddMatchManuallyButton'
import RequestCard from 'components/requests/RequestCard'
import CategoryIcon from 'components/services/CategoryIcon'
import { useReclaimTaskData } from 'hooks/reclaimTaskHooks'
import { ReclaimTaskDetailProps } from 'interfaces/Reclaims'

const ReclaimTaskOverview = ({ reclaim, notes }: ReclaimTaskDetailProps) => {
    const { request, service, provider, isLoading, hasData } = useReclaimTaskData(reclaim)
    const providerId = reclaim.providerId
    const filterIdAssociations = `p_${providerId}`
    const filterIdProviderMatches = `provider_matches_${providerId}`

    const providerMatchesDefaultOptions = {
        providerIds: [providerId],
        pageSize: 10,
    }

    const logOptions = {
        providerIds: [providerId],
        pageSize: 10,
        logTypes: [
            'filters',
            'geo',
            'leadSettingsLimits',
            'week_limit_reached',
            'clear_week_limit',
        ],
    }

    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <ReclaimDetails reclaim={reclaim} request={request} />
                    <Divider m />
                    {isLoading ? (
                        <LoadingDots />
                    ) : hasData && request && service ? (
                        <>
                            <Card
                                header={<span>{service.name} Request</span>}
                                icon={<CategoryIcon color="blue" id={service?.category_id} />}>
                                <RequestCard requestId={request.request_id} />
                            </Card>
                            <Divider m />
                            <Card header="Matches (Request)" icon="compare_arrows">
                                <MatchFilter
                                    filterId={filterIdAssociations}
                                    compact
                                    hidePagination
                                    defaultOptions={{ requestIds: [request.request_id] }}
                                />
                                <MatchList
                                    showCols={[
                                        'id',
                                        'created',
                                        'name',
                                        'firstChoice',
                                        'from_dashboard',
                                        'charge',
                                        'confirm',
                                        'stripes',
                                        'nrMsgs',
                                        'partialMatch',
                                        'reclaim',
                                        'leadScore',
                                    ]}
                                    defaultOptions={{ pageSize: 10 }}
                                    filterId={filterIdAssociations}
                                />
                                <Divider />
                                <AddMatchManuallyButton
                                    serviceId={service.service_id}
                                    requestId={request.request_id}
                                    filterId={filterIdAssociations}
                                />
                            </Card>
                            <Divider m />
                            {provider ? (
                                <Card header="Matches (Provider)" icon="compare_arrows">
                                    <MatchList
                                        filterId={filterIdProviderMatches}
                                        defaultOptions={{
                                            ...providerMatchesDefaultOptions,
                                            forceFetch: true,
                                        }}
                                        showCols={[
                                            'charge',
                                            'firstChoice',
                                            'nrMsgs',
                                            'created',
                                            'userName',
                                            'stripes',
                                            'reclaim',
                                            'partialMatch',
                                            'leadScore',
                                        ]}
                                        hideExport
                                    />
                                </Card>
                            ) : (
                                <LoadingDots />
                            )}
                        </>
                    ) : (
                        <p className="text-md text-neutral-500 italic">
                            No request or service data found.
                        </p>
                    )}
                    <Divider m />
                </div>

                <div>
                    <Card header="Reclaim Info" icon="help_outline">
                        <Divider sm />
                        <ReclaimTaskDetails
                            reclaim={reclaim}
                            providerId={providerId}
                            notes={notes}
                        />
                    </Card>
                    <Divider m />
                    <Card header="Provider Reclaims" icon="mood_bad">
                        <ReclaimsList
                            filterId={filterIdAssociations}
                            defaultOptions={{
                                providerId: providerId,
                                pageSize: 10,
                            }}
                            showCols={[
                                'reclaim_id',
                                'status',
                                'created',
                                'phoneNumber',
                                'leadName',
                                'action',
                                'notes',
                            ]}
                            showFilters={['leadType', 'status', 'search']}
                            hideExport
                            compact
                        />
                    </Card>
                    <Divider m />
                    <Card header="Provider Notes" icon="speaker_notes">
                        <NotesFilter
                            filterId={filterIdAssociations}
                            defaultOptions={{
                                providerIds: [providerId],
                                pageSize: 10,
                            }}
                            newEntityOptions={{ type: 'serviceProviders', foreign_key: providerId }}
                        />
                        <NotesList filterId={filterIdAssociations} />
                    </Card>
                    <Divider m />
                    <Card header="Log List" icon="edit_note">
                        <LogsList filterId={filterIdAssociations} options={logOptions} />
                    </Card>
                    <Divider m />
                </div>
            </div>
        </div>
    )
}

export default ReclaimTaskOverview
